import styled, { css } from "styled-components";
import type { TextVariantsType } from "./Text";
import type { UscColorType, UscSpacingType } from "../../../../../../../shared-components/lexus/styles/Variables.types";
import { UscColorProps, UscSpacingProps } from "../../../../../../../shared-components/lexus/styles/Variables.types";
import NumberedDisclaimerIcon from "./NumberedDisclaimerIcon.styles";

// STYLES
export type SpacingType = {
    m?: UscSpacingType;
    mt?: UscSpacingType;
    mb?: UscSpacingType;
    ml?: UscSpacingType;
    mr?: UscSpacingType;
    mx?: UscSpacingType;
    my?: UscSpacingType;
};

const globalStyle = css<{ color?: UscColorType; uppercase?: boolean } & SpacingType>`
    --_text-transform: var(--text-transform, none);
    --_font-family: var(--font-family, var(--font-regular));

    margin-block: 0;
    font-family: var(--_font-family);
    text-transform: var(--_text-transform);
    color: var(--color-primary-black);

    ${({ color }) =>
        color &&
        css`
            color: var(${UscColorProps[color]});
        `};

    ${({ uppercase }) =>
        uppercase &&
        css`
            --text-transform: uppercase;
        `};

    ${(props) =>
        props.m &&
        css`
            margin: var(${UscSpacingProps[props.m]});
        `}
    ${(props) =>
        props.mt &&
        css`
            margin-top: var(${UscSpacingProps[props.mt]});
        `}
    ${(props) =>
        props.mb &&
        css`
            margin-bottom: var(${UscSpacingProps[props.mb]});
        `}
    ${(props) =>
        props.ml &&
        css`
            margin-inline-start: var(${UscSpacingProps[props.ml]});
        `}
    ${(props) =>
        props.mr &&
        css`
            margin-inline-end: var(${UscSpacingProps[props.mr]});
        `}
    ${(props) =>
        props.mx &&
        css`
            margin-inline: var(${UscSpacingProps[props.mx]});
        `}
    ${(props) =>
        props.my &&
        css`
            margin-block: var(${UscSpacingProps[props.my]});
        `}

    ${NumberedDisclaimerIcon} {
        margin-inline-start: calc(var(--spacing-1) / 2);
    }
`;

const h1Style = css`
    ${globalStyle};
    font-size: 1.4rem;
    line-height: 2rem;
`;

const h2Style = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-semi-bold);
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.6rem;
`;

const h3Style = css`
    ${globalStyle};
    font-size: 1.4rem;
    line-height: 1.8rem;
`;

const h4Style = css`
    ${globalStyle};
    font-size: 1.2rem;
    line-height: 1.6rem;
`;

const h5Style = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-light);
    font-size: 1rem;
    line-height: 1.4rem;
`;

const ctaStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-semi-bold);
    font-size: 0.7rem;
    line-height: 1rem;
`;

const paragraphBodyBookStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    font-size: 1rem;
    line-height: 1.4rem;
`;

const paragraphBodyRegularStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-regular);
    font-size: 1rem;
    line-height: 1.4rem;
`;

const paragraphSmallBookStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-book);
    font-size: 0.8rem;
    line-height: 1.2rem;
`;

const paragraphSmallRegularStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-regular);
    font-size: 0.8rem;
    line-height: 1.2rem;
`;

const paragraphSmallBoldStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-bold);
    font-size: 0.8rem;
    line-height: 1.2rem;
`;

const legalStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-regular);
    font-size: 0.6rem;
    line-height: 1rem;
`;

const badgeStyle = css`
    ${globalStyle};
    /* stylelint-disable-next-line order/order */
    --font-family: var(--font-bold);
    font-size: 0.7rem;
    line-height: 1.2rem;
`;

// COMPONENT
export const TextVariants = {
    h1: h1Style,
    h2: h2Style,
    h3: h3Style,
    h4: h4Style,
    h5: h5Style,
    cta: ctaStyle,
    legal: legalStyle,
    badge: badgeStyle,
    "paragraph-body-book": paragraphBodyBookStyle,
    "paragraph-body-regular": paragraphBodyRegularStyle,
    "paragraph-small-book": paragraphSmallBookStyle,
    "paragraph-small-regular": paragraphSmallRegularStyle,
    "paragraph-small-bold": paragraphSmallBoldStyle,
};

const TextVariant = styled.span<{
    variant: TextVariantsType;
    color?: UscColorType;
    spacing?: SpacingType;
    uppercase?: boolean;
}>`
    ${({ color }) =>
        color &&
        css`
            ${color &&
            css`
                color: var(${UscColorProps[color]});
            `}
        `}
    ${({ variant }) =>
        variant &&
        css`
            ${TextVariants[variant]};
        `}
    
    ${(props) =>
        props.spacing &&
        props.spacing.m &&
        css`
            margin: var(${UscSpacingProps[props.spacing.m]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mt &&
        css`
            margin-top: var(${UscSpacingProps[props.spacing.mt]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mb &&
        css`
            margin-bottom: var(${UscSpacingProps[props.spacing.mb]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.ml &&
        css`
            margin-inline-start: var(${UscSpacingProps[props.spacing.ml]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mr &&
        css`
            margin-inline-end: var(${UscSpacingProps[props.spacing.mr]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.mx &&
        css`
            margin-inline: var(${UscSpacingProps[props.spacing.mx]});
        `}
    ${(props) =>
        props.spacing &&
        props.spacing.my &&
        css`
            margin-block: var(${UscSpacingProps[props.spacing.my]});
        `}
`;

export default TextVariant;
